/* translator.js - Strick-Übersetzer.

   Translates English knitting patterns into German / Italian / French /
   Spanish using the dictionary at window.translatorDicts. The translation
   is a sophisticated dictionary substitution:
     1. Sort terms by length (longest first) so multi-word terms win over
        their fragments (e.g. "long tail cast on" replaces before "co").
     2. Replace complex terms first, formatted as "Translation (Original)".
     3. Replace simple terms, formatted as "Original (Translation)".
     4. Handle stitch counts (k2, p3 → 2 re, 3 li).
     5. Handle row/round numbering and repeat patterns.
     6. Convert inches → centimeters.
     7. Append a beta notice.
     8. Render a highlighted HTML version with tooltips for complex
        techniques (which include short German descriptions and an
        optional YouTube link).
*/
(function () {
  'use strict';

  const root = document.querySelector('[data-tx-app]');
  if (!root) return;

  // ── DOM refs ─────────────────────────────────────────────────────────────

  const inputEl     = root.querySelector('[data-tx-input]');
  const outputEl    = root.querySelector('[data-tx-output]');
  const highlightEl = root.querySelector('[data-tx-highlighted]');
  const countEl     = root.querySelector('[data-tx-count]');
  const statsEl     = root.querySelector('[data-tx-stats]');
  const langSelect  = root.querySelector('[data-tx-language]');
  const sampleBtn   = root.querySelector('[data-tx-sample]');
  const clearBtn    = root.querySelector('[data-tx-clear]');
  const translateBtn = root.querySelector('[data-tx-translate]');
  const copyBtn     = root.querySelector('[data-tx-copy]');
  const copyLabel   = root.querySelector('[data-tx-copy-label]');
  const glossaryEl  = root.querySelector('[data-tx-glossary]');
  const noticeEl    = root.querySelector('[data-tx-notice]');
  const tooltip     = document.getElementById('tx-tooltip');

  // ── Dictionary state ─────────────────────────────────────────────────────

  let currentLang = 'de';
  let simpleDict = {};
  let complexDict = {};
  let ui = {};

  function setLanguage(lang) {
    if (!window.translatorDicts || !window.translatorDicts[lang]) {
      console.warn('[translator] dictionary not loaded for', lang);
      return;
    }
    currentLang = lang;
    simpleDict  = window.translatorDicts[lang].simple || {};
    complexDict = window.translatorDicts[lang].complex || {};
    ui          = window.translatorDicts[lang].ui || {};
    updateGlossary();
  }

  function updateGlossary() {
    if (!glossaryEl) return;
    glossaryEl.querySelectorAll('.tx-glossary-card[data-term]').forEach(card => {
      const term = card.dataset.term;
      const out = card.querySelector('.tx-glossary-card__translation');
      if (!out) return;
      if (simpleDict[term]) {
        out.textContent = simpleDict[term];
      } else if (complexDict[term]) {
        out.textContent = complexDict[term].translation;
      } else {
        out.textContent = '-';
      }
    });
  }

  // ── Char count ──────────────────────────────────────────────────────────

  function updateCount() {
    if (!inputEl || !countEl) return;
    const n = inputEl.value.length;
    countEl.textContent = `${n} Zeichen`;
  }

  // ── Translation ─────────────────────────────────────────────────────────

  function translate() {
    if (!inputEl || !outputEl || !highlightEl) return;
    const text = inputEl.value;
    if (!text.trim()) {
      showNotice(bfT('tx_enter_text', 'Bitte gib zuerst einen englischen Text ein.'), { error: true });
      return;
    }

    let translated = text;
    const sortedSimple  = Object.keys(simpleDict).sort((a, b) => b.length - a.length);
    const sortedComplex = Object.keys(complexDict).sort((a, b) => b.length - a.length);

    // Complex first - they're longer, more contextual.
    sortedComplex.forEach(term => {
      const re = new RegExp('\\b' + escRe(term) + '\\b', 'gi');
      translated = translated.replace(re, () => `${complexDict[term].translation} (${term})`);
    });

    // Simple terms (preserve original in parens for review).
    sortedSimple.forEach(term => {
      const re = new RegExp('\\b' + escRe(term) + '\\b', 'gi');
      translated = translated.replace(re, (match) => `${match} (${simpleDict[term]})`);
    });

    // Stitch counts (k2, p3) → "2 re", "3 li" (or whatever language UI says).
    const stK = ui.stitchK || 're';
    const stP = ui.stitchP || 'li';
    translated = translated.replace(/\b([kp])(\d+)\b/gi, (_m, s, n) => {
      return `${n} ${s.toLowerCase() === 'k' ? stK : stP}`;
    });

    // Row / round patterns
    const rowS  = ui.row    || 'Reihe';
    const rowP  = ui.rows   || bfT('col_rows_label', 'Reihen');
    const rndS  = ui.round  || 'Runde';
    const rndP  = ui.rounds || 'Runden';
    const tWord = ui.times  || 'mal';
    translated = translated
      .replace(/\brow\s+(\d+):/gi, `${rowS} $1:`)
      .replace(/\brows\s+(\d+)-(\d+)/gi, `${rowP} $1-$2`)
      .replace(/\bround\s+(\d+):/gi, `${rndS} $1:`)
      .replace(/\brounds\s+(\d+)-(\d+)/gi, `${rndP} $1-$2`)
      .replace(/\(([^)]+)\)(\s*\d+)(\s*times)/gi, `($1)$2 ${tWord}`);

    // Inches → cm
    translated = translated
      .replace(/(\d+(?:\.\d+)?)\s*inches?\b/gi, (_m, num) => {
        return `${Math.round(parseFloat(num) * 2.54 * 10) / 10} cm`;
      })
      .replace(/(\d+)\/(\d+)\s*inch(?:es)?/gi, (_m, num, den) => {
        return `${Math.round((parseInt(num) / parseInt(den)) * 2.54 * 10) / 10} cm`;
      });

    const beta = ui.betaNotice || bfT('tx_beta_clipboard', '[BONIFAKTUR BETA-Übersetzung - Es können Fehler passieren. Wer einen entdeckt, mag ihn uns gern melden.]');
    translated += `\n\n---\n${beta}`;

    outputEl.value = translated;

    // Build highlighted HTML version (terms wrapped in spans).
    let html = escHtml(translated);
    const placeholders = new Map();
    let pIdx = 0;

    // Highlight complex terms (they appear as "Translation (English)").
    sortedComplex.forEach(eng => {
      const trans = complexDict[eng].translation;
      const pattern = `${escRe(trans)}\\s*\\(${escRe(eng)}\\)`;
      const re = new RegExp(pattern, 'g');
      html = html.replace(re, () => {
        const ph = `__TX_C_${pIdx++}__`;
        placeholders.set(ph,
          `<span class="highlighted-complex" data-term="${escAttr(eng)}">` +
          `${trans} <span class="original-term">(${eng})</span>` +
          `</span>`);
        return ph;
      });
    });

    // Highlight simple terms (they appear as "English (Translation)").
    sortedSimple.forEach(eng => {
      const trans = simpleDict[eng];
      const pattern = `(\\b${escRe(eng)}\\b)\\s*\\(\\s*(${escRe(trans)})\\s*\\)`;
      const re = new RegExp(pattern, 'gi');
      html = html.replace(re, (_m, e, t) => {
        const ph = `__TX_S_${pIdx++}__`;
        placeholders.set(ph, `${e} (<span class="highlighted-term">${t}</span>)`);
        return ph;
      });
    });

    // Highlight stitch counts that we already converted.
    const stPat = `(\\b[kp]\\d+\\b)\\s*\\(\\s*(${escRe(stK)}|${escRe(stP)})\\s*\\)`;
    html = html.replace(new RegExp(stPat, 'gi'), (_m, e, t) => {
      const ph = `__TX_K_${pIdx++}__`;
      placeholders.set(ph, `${e} (<span class="highlighted-term">${t}</span>)`);
      return ph;
    });

    placeholders.forEach((v, k) => { html = html.replace(k, v); });
    html = html.replace(/\n/g, '<br>');

    highlightEl.innerHTML = html;
    highlightEl.hidden = false;
    outputEl.style.display = 'none';

    showStats(text);
    bindTooltips();
  }

  // ── Stats ───────────────────────────────────────────────────────────────

  function showStats(original) {
    if (!statsEl) return;
    let simpleCount = 0, complexCount = 0;
    Object.keys(simpleDict).forEach(term => {
      const m = original.match(new RegExp('\\b' + escRe(term) + '\\b', 'gi'));
      if (m) simpleCount += m.length;
    });
    Object.keys(complexDict).forEach(term => {
      const m = original.match(new RegExp('\\b' + escRe(term) + '\\b', 'gi'));
      if (m) complexCount += m.length;
    });
    const total = simpleCount + complexCount;
    statsEl.textContent = total === 0
      ? bfT('tx_no_terms', 'Keine Begriffe gefunden')
      : `${total} Begriffe übersetzt (${simpleCount} einfach, ${complexCount} komplex)`;
  }

  // ── Tooltip for complex terms ──────────────────────────────────────────

  let tooltipHideTimer = 0;
  let tooltipMouseOver = false;

  function bindTooltips() {
    if (!highlightEl || !tooltip) return;
    highlightEl.querySelectorAll('.highlighted-complex').forEach(el => {
      el.addEventListener('mouseenter', () => {
        clearTimeout(tooltipHideTimer);
        showTooltip(el);
      });
      el.addEventListener('mouseleave', () => {
        tooltipHideTimer = setTimeout(() => {
          if (!tooltipMouseOver) hideTooltip();
        }, 120);
      });
    });
  }

  if (tooltip) {
    tooltip.addEventListener('mouseenter', () => {
      tooltipMouseOver = true;
      clearTimeout(tooltipHideTimer);
    });
    tooltip.addEventListener('mouseleave', () => {
      tooltipMouseOver = false;
      tooltipHideTimer = setTimeout(hideTooltip, 120);
    });
  }

  function showTooltip(target) {
    const term = target.dataset.term;
    const info = complexDict[term];
    if (!info) return;

    const videoLink = info.videoId
      ? `<a href="https://youtube.com/watch?v=${info.videoId}" target="_blank" rel="noopener noreferrer">Video-Tutorial</a>`
      : '';
    tooltip.innerHTML = `${escHtml(info.description || '')}${videoLink ? '<br>' + videoLink : ''}`;
    tooltip.hidden = false;
    positionTooltip(target);
    requestAnimationFrame(() => { tooltip.dataset.visible = '1'; });
  }

  function hideTooltip() {
    if (!tooltip) return;
    tooltip.dataset.visible = '0';
    tooltip.hidden = true;
  }

  function positionTooltip(target) {
    const rect = target.getBoundingClientRect();
    const ttRect = tooltip.getBoundingClientRect();
    const sx = window.pageXOffset;
    const sy = window.pageYOffset;
    const margin = 12;

    let top = sy + rect.top - ttRect.height - margin;
    let left = sx + rect.left + rect.width / 2 - ttRect.width / 2;

    if (left < margin) left = margin;
    if (left + ttRect.width > window.innerWidth - margin) {
      left = window.innerWidth - ttRect.width - margin;
    }
    if (top < sy + margin) {
      top = sy + rect.bottom + margin;
    }
    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;
  }

  // ── Sample / clear / copy ───────────────────────────────────────────────

  function loadSample() {
    if (!inputEl) return;
    inputEl.value =
      'Cast on 72 stitches using long tail cast on.\n' +
      '\n' +
      'Row 1 (RS): *K2, p2; repeat from * to end.\n' +
      'Row 2 (WS): *P2, k2; repeat from * to end.\n' +
      'Repeat rows 1-2 until piece measures 8 inches.\n' +
      '\n' +
      'Decrease row: K2tog across (36 stitches remain).\n' +
      'Work 6 rows in stockinette stitch.\n' +
      '\n' +
      'Shape top:\n' +
      'Row 1: *K4, ssk; repeat from * to end.\n' +
      'Row 2 and all WS rows: Purl.\n' +
      'Row 3: *K3, k2tog; repeat from * to end.\n' +
      'Row 5: *K2, ssk; repeat from * to end.\n' +
      '\n' +
      'Break yarn, thread through remaining stitches.\n' +
      'Bind off all stitches. Weave in ends and block.';
    updateCount();
  }

  function clearAll() {
    if (inputEl)  inputEl.value = '';
    if (outputEl) { outputEl.value = ''; outputEl.style.display = ''; }
    if (highlightEl) { highlightEl.innerHTML = ''; highlightEl.hidden = true; }
    if (statsEl)  statsEl.textContent = '';
    updateCount();
  }

  async function copyOutput() {
    if (!outputEl) return;
    const fromHighlight = highlightEl && !highlightEl.hidden ? highlightEl.innerText : null;
    const text = fromHighlight || outputEl.value;
    if (!text || !text.trim()) {
      showNotice(bfT('tx_nothing_copy', 'Es gibt noch nichts zum Kopieren.'), { error: true });
      return;
    }

    const ok = await copyText(text);
    if (ok) {
      if (copyBtn) copyBtn.classList.add('is-success');
      if (copyLabel) copyLabel.textContent = 'Kopiert ✓';
      setTimeout(() => {
        if (copyBtn) copyBtn.classList.remove('is-success');
        if (copyLabel) copyLabel.textContent = bfT('copy', 'Kopieren');
      }, 2000);
    } else {
      // Iframe / permissions-policy block - fall through to a manual select.
      outputEl.style.display = '';
      outputEl.focus();
      outputEl.select();
      showNotice(bfT('tx_copy_blocked', 'Kopieren ist hier blockiert - bitte Text mit ⌘C / Ctrl+C kopieren.'), { duration: 4500 });
    }
  }

  async function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try { await navigator.clipboard.writeText(text); return true; }
      catch (_) { /* fall through */ }
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch (_) { return false; }
  }

  // ── Inline notice ───────────────────────────────────────────────────────

  let noticeTimer = 0;
  function showNotice(message, opts) {
    if (!noticeEl) { console.warn('[translator]', message); return; }
    const isError = opts && opts.error;
    noticeEl.textContent = message;
    noticeEl.classList.toggle('tx-notice--error', !!isError);
    noticeEl.hidden = false;
    void noticeEl.offsetHeight;
    noticeEl.classList.add('is-visible');
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => {
      noticeEl.classList.remove('is-visible');
      setTimeout(() => { noticeEl.hidden = true; }, 220);
    }, (opts && opts.duration) || 3000);
  }

  // ── Helpers ─────────────────────────────────────────────────────────────

  function escHtml(text) {
    const d = document.createElement('div');
    d.textContent = text;
    return d.innerHTML;
  }
  function escRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
  function escAttr(s) { return s.replace(/"/g, '&quot;').replace(/&/g, '&amp;'); }

  // ── Init ────────────────────────────────────────────────────────────────

  function init() {
    if (!window.translatorDicts) {
      console.warn('[translator] window.translatorDicts is missing - dictionary asset failed to load');
      showNotice(bfT('tx_dict_error', 'Wörterbuch konnte nicht geladen werden - bitte Seite neu laden.'), { error: true, duration: 6000 });
      return;
    }

    setLanguage('de');

    if (inputEl)     inputEl.addEventListener('input', updateCount);
    if (translateBtn) translateBtn.addEventListener('click', translate);
    if (clearBtn)    clearBtn.addEventListener('click', clearAll);
    if (copyBtn)     copyBtn.addEventListener('click', copyOutput);
    if (sampleBtn)   sampleBtn.addEventListener('click', loadSample);

    if (langSelect) {
      langSelect.addEventListener('change', () => {
        setLanguage(langSelect.value);
        if (inputEl && inputEl.value.trim()) translate();
      });
    }

    updateCount();
    updateGlossary();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
